import { useState, useEffect, type FormEvent } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { api, setAccessToken } from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import { hideSplash } from '../../capacitorInit';

export function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const taggedEventId = searchParams.get('taggedEventId');
  const navigate = useNavigate();
  const { user, refresh } = useAuth();

  useEffect(() => { hideSplash(); }, []);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [useSeparateAccount, setUseSeparateAccount] = useState(false);

  async function acceptAsCurrentUser() {
    if (!token) return;
    setError('');
    setLoading(true);
    try {
      await api.post('/account/members/accept-invite', { token });
      navigate(taggedEventId ? `/events/${taggedEventId}` : '/events');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } }).response?.data?.error || 'Failed to accept invite. The link may have expired.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  function signInInstead() {
    if (token) sessionStorage.setItem('pendingInviteToken', token);
    if (taggedEventId) sessionStorage.setItem('pendingTaggedEventId', taggedEventId);
  }

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F8F9FF] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg w-full max-w-md p-8 text-center">
          <h1 className="text-3xl font-serif font-bold text-indigo-700 mb-4">EventsCarousel</h1>
          <p className="text-red-600 text-sm">Invalid or missing invite link. Please check your email and try again.</p>
        </div>
      </div>
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post('/auth/accept-invite', { token, password });
      localStorage.setItem('accessToken', data.accessToken);
      setAccessToken(data.accessToken);
      localStorage.setItem('carousel_tour_pending', 'true');
      await refresh();
      navigate(taggedEventId ? `/events/${taggedEventId}` : '/events');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } }).response?.data?.error || 'Failed to accept invite. The link may have expired.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#F8F9FF] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-md p-8">
        <h1 className="text-3xl font-serif font-bold text-indigo-700 mb-2">EventsCarousel</h1>
        <p className="text-stone-500 mb-1 text-sm">You've been invited to join EventsCarousel.</p>

        {user && !useSeparateAccount ? (
          <>
            <p className="text-stone-400 mb-4 text-sm">
              You're signed in as {user.email}. Accept the invite to add it to this account.
            </p>
            {error && <p className="text-red-600 text-sm mb-4">{error}</p>}
            <button
              type="button"
              onClick={acceptAsCurrentUser}
              disabled={loading}
              className="w-full bg-indigo-600 text-white rounded-lg py-2 text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Accepting…' : `Accept as ${user.name}`}
            </button>
            <p className="mt-4 text-center text-sm text-stone-500">
              <button type="button" onClick={() => setUseSeparateAccount(true)} className="text-indigo-600 font-medium hover:underline">
                Create a separate account instead
              </button>
            </p>
          </>
        ) : (
          <>
            <p className="text-stone-400 mb-4 text-sm">Create a password to get started.</p>
            {user && (
              <p className="text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4 text-xs">
                You're currently signed in as {user.email}. Continuing will switch your active session to this new invited account.{' '}
                <button type="button" onClick={() => setUseSeparateAccount(false)} className="underline font-medium">Go back</button>
              </p>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  autoFocus
                  minLength={8}
                  className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                  className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {error && <p className="text-red-600 text-sm">{error}</p>}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-indigo-600 text-white rounded-lg py-2 text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {loading ? 'Creating account…' : 'Create Account'}
              </button>
            </form>

            {!user && (
              <p className="mt-6 text-center text-sm text-stone-500">
                Already have an EventsCarousel account?{' '}
                <Link to="/login" onClick={signInInstead} className="text-indigo-600 font-medium hover:underline">Sign in instead</Link>
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
